import classNames from "classnames";
import noop from "lodash/noop";
import { FunctionComponent, useState } from "react";
import { useIntl } from "react-intl";
import Tag from "@Components/atoms/Tag";
import CheckoutAutocompleteInput from "@Components/molecules/cart/CheckoutAutocompleteInput";
import CheckoutPickupLocations from "@Components/molecules/cart/CheckoutPickupLocations";
import CheckoutPickupLocationsSkeleton from "@Components/molecules/cart/CheckoutPickupLocationsSkeleton";
import CheckoutStep from "@Components/molecules/cart/CheckoutStep";
import RadioCard from "@Components/molecules/RadioCard";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconSearch } from "@Icons/16x16/search.svg";
import { ReactComponent as IconLocation } from "@Icons/colored/location.svg";
import { AutocompletePlace, MondialRelayLocation } from "@Types/addresses";
import { Currency } from "@Types/api/price";
import { FormError } from "@Types/error";
import { ShippingMethod, ShippingMethodName } from "@Types/shipping";
import { isPickupSelected } from "@Utils/cart/addresses";
import { getInputErrorMessages } from "@Utils/error";
import "./style.scss";

interface Props {
  currency: Currency;
  disabled: boolean;
  onChangeShippingMethod: (method: ShippingMethodName) => void;
  onSelectAddressForPickup: (place: AutocompletePlace | undefined) => void;
  onSelectPickupLocation: (location?: MondialRelayLocation) => void;
  pickupLocations: MondialRelayLocation[];
  selectedPickupLocation: MondialRelayLocation | undefined;
  shippingErrors: FormError[];
  shippingMethods: ShippingMethod[];
  waitingPickupList: boolean;
}

const CheckoutShippingStep: FunctionComponent<Props> = ({
  currency,
  disabled,
  onChangeShippingMethod,
  onSelectAddressForPickup,
  onSelectPickupLocation,
  pickupLocations,
  selectedPickupLocation,
  shippingErrors,
  shippingMethods,
  waitingPickupList,
}) => {
  const { isSmallScreen } = useBreakpoint();
  const intl = useIntl();
  const [pickupAddressInput, setPickupAddressInput] = useState<string | undefined>("");

  return (
    <div className="CheckoutShippingStep">
      <CheckoutStep
        buttonText="" // TODO: The button is not display on this PR
        disabled={disabled}
        displayButton={false}
        editable={false}
        icon={<IconLocation />}
        onEdit={noop}
        onValidate={noop}
        title={intl.formatMessage({ id: "cheerz_CheckoutShippingStep_title" })}
        validated={shippingMethods.some((shippingMethod) => shippingMethod.selected === true)}
      >
        <div className="CheckoutShippingStep-content">
          {intl.formatMessage({ id: "cheerz_CheckoutShippingStep_description" })}

          {shippingMethods
            .sort((a, b) => a.priority - b.priority)
            .map((shippingMethod) => {
              const price = !!shippingMethod.minPrice ? (
                <div className="CheckoutShippingStep-RadioCard-content-price">
                  <span className="CheckoutShippingStep-RadioCard-content-price-text">
                    {intl.formatMessage({ id: "cheerz_CheckoutShippingStep_price" })}
                  </span>
                  <span className="CheckoutShippingStep-RadioCard-content-price-value">
                    {intl.formatNumber(shippingMethod.minPrice / 100, {
                      currency,
                      style: "currency",
                    })}
                  </span>
                </div>
              ) : null;
              const tag = !!shippingMethod.tag ? (
                <Tag backgroundColor={shippingMethod.tag.color} size="s">
                  {intl.formatMessage({ id: shippingMethod.tag.labelId })}
                </Tag>
              ) : null;

              return (
                <RadioCard
                  checked={shippingMethod.selected}
                  disabled={disabled}
                  hasError={false}
                  key={shippingMethod.name}
                  logo={shippingMethod.logo ? shippingMethod.logo : undefined}
                  onClick={() => onChangeShippingMethod(shippingMethod.name)}
                  title={
                    <>
                      {intl.formatMessage({ id: shippingMethod.labelId })}
                      {!isSmallScreen && tag}
                    </>
                  }
                >
                  <div className="CheckoutShippingStep-RadioCard-content-description">
                    {intl.formatMessage({ id: shippingMethod.descriptionId })}
                    {!isSmallScreen && price}
                  </div>
                  {isSmallScreen && (
                    <div
                      className={classNames("CheckoutShippingStep-RadioCard-content-informations", {
                        "CheckoutShippingStep-RadioCard-content-informations--withTag":
                          !!shippingMethod.tag,
                      })}
                    >
                      {!!shippingMethod.tag && tag}
                      {price}
                    </div>
                  )}
                </RadioCard>
              );
            })}

          {!isSmallScreen &&
            isPickupSelected(
              shippingMethods.find((shippingMethod) => shippingMethod.selected === true)?.name
            ) && (
              <div
                className={classNames("CheckoutShippingStep-content-pickup", {
                  "CheckoutShippingStep-content-pickup--loading": waitingPickupList,
                  "CheckoutShippingStep-content-pickup--withList": pickupLocations.length > 0,
                })}
              >
                <CheckoutAutocompleteInput
                  disabled={disabled}
                  errorMessage={getInputErrorMessages(shippingErrors, "pickUpAddress")}
                  icon={<IconSearch />}
                  onChange={(value) => setPickupAddressInput(value)}
                  onDismiss={() => {
                    setPickupAddressInput("");
                    onSelectAddressForPickup(undefined);
                  }}
                  onlyDisplayStreet={false}
                  onSelect={(place) => {
                    setPickupAddressInput(place.formattedAddress);
                    onSelectAddressForPickup(place);
                  }}
                  value={pickupAddressInput}
                />
                <div className="CheckoutShippingStep-content-CheckoutAutocompleteInput-description">
                  {intl.formatMessage({
                    id: "cheerz_CheckoutShippingBottomSheet_description",
                  })}
                </div>
                {waitingPickupList ? (
                  <CheckoutPickupLocationsSkeleton />
                ) : (
                  pickupLocations.length > 0 &&
                  !selectedPickupLocation && (
                    <CheckoutPickupLocations
                      locations={pickupLocations}
                      onSelectLocation={(location: MondialRelayLocation) =>
                        onSelectPickupLocation(location)
                      }
                    />
                  )
                )}
              </div>
            )}
        </div>
      </CheckoutStep>
    </div>
  );
};

export default CheckoutShippingStep;
